import { inject, Injectable } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpApiService } from '@core/http-legacy';
import {
  AutomationTemplate,
  WorkflowAutomation,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';

@Injectable({ providedIn: 'root' })
export class JobWorkflowAutomationsHttpClient {
  private readonly httpApi = inject(HttpApiService);

  public loadJobAutomations(jobId: number): Observable<WorkflowAutomation[]> {
    return this.httpApi
      .get(`workflow_automations/offers/${jobId}/automations`, null)
      .pipe(map(data => data.workflowAutomations));
  }

  public createAutomation(
    workflowAutomation: WorkflowAutomationCreatePayload,
    jobId: number,
  ): Observable<WorkflowAutomation> {
    return this.httpApi
      .post(`workflow_automations/offers/${jobId}/automations`, null, {
        workflowAutomation: {
          global: false,
          offerAssignments: [
            {
              offerId: jobId,
              enabled: true,
            },
          ],
          ...workflowAutomation,
        },
      })
      .pipe(map(data => data.workflowAutomation));
  }

  public updateAutomation(
    workflowAutomation: WorkflowAutomationUpdatePayload,
    jobId: number,
  ): Observable<WorkflowAutomation> {
    return this.httpApi
      .patch(`workflow_automations/offers/${jobId}/automations/${workflowAutomation.id}`, null, {
        workflowAutomation: {
          global: false,
          offerAssignments: [
            {
              offerId: jobId,
              enabled: true,
            },
          ],
          ...workflowAutomation,
        },
      })
      .pipe(map(data => data.workflowAutomation));
  }

  public removeAutomation(id: number, jobId: number): Observable<WorkflowAutomation> {
    return this.httpApi
      .delete(`workflow_automations/offers/${jobId}/automations/${id}`, null)
      .pipe(map(data => data.workflowAutomation));
  }

  public duplicateAutomation(
    id: number,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<WorkflowAutomation> {
    return this.httpApi
      .post(
        `workflow_automations/offers/${additionalData.jobId}/automations/${id}/duplicate`,
        null,
        {},
      )
      .pipe(map(data => data.workflowAutomation));
  }

  public fetchAutomationOptions(jobId: number): Observable<WorkflowAutomationOptionsData> {
    return this.httpApi.get(`/workflow_automations/offers/${jobId}`, null);
  }

  public fetchAutomationTemplates(jobId: number): Observable<AutomationTemplate[]> {
    return this.httpApi
      .get(`workflow_automations/offers/${jobId}/templates`, null)
      .pipe(map(data => data.templates));
  }
}
